import React, { useState, useEffect, useCallback } from 'react';
import { Play, RefreshCw, Search, LayoutList, Lock, Route, Database, GitMerge, Wrench, FlaskConical, SlidersHorizontal } from 'lucide-react';
import apiClient from '../../services/apiService';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import Skeleton from '../common/Skeleton';
import { statusBadgeCss } from '../../utils/statusVariant';

interface TestResult {
  id: string; testName: string; testFile: string;
  status: 'PASSED' | 'FAILED' | 'RUNNING' | 'SKIPPED';
  duration: number; message?: string; timestamp: string;
  category: 'unit' | 'integration' | 'auth' | 'routes' | 'models' | 'utils';
}

interface TestSuite {
  name: string; totalTests: number; passedTests: number;
  failedTests: number; skippedTests: number; duration: number;
  status: 'PASSED' | 'FAILED' | 'RUNNING'; timestamp: string;
}

type SectionKey = 'ALL' | 'AUTH' | 'ROUTES' | 'MODELS' | 'INTEGRATION' | 'UTILS' | 'UNIT';

const SECTIONS: { key: SectionKey; label: string; icon: React.ElementType }[] = [
  { key: 'ALL',         label: 'All Tests',   icon: LayoutList   },
  { key: 'AUTH',        label: 'Auth',         icon: Lock         },
  { key: 'ROUTES',      label: 'Routes',       icon: Route        },
  { key: 'MODELS',      label: 'Models',       icon: Database     },
  { key: 'INTEGRATION', label: 'Integration',  icon: GitMerge     },
  { key: 'UTILS',       label: 'Utils',        icon: Wrench       },
  { key: 'UNIT',        label: 'Unit',         icon: FlaskConical },
];


const QUICK_RUNS: [string, string][] = [
  ['Auth Tests',        'tests/backend/test_auth.py'],
  ['Backend Tests',     'tests/backend/'],
  ['Integration Tests', 'tests/integration/'],
];

const TestResultsTab: React.FC = () => {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [testSuites, setTestSuites]   = useState<TestSuite[]>([]);
  const [loading, setLoading]         = useState(false);
  const [toast, setToast]             = useState<ToastMessage | null>(null);
  const [running, setRunning]         = useState(false);
  const [activeSection, setActiveSection] = useState<SectionKey>('ALL');
  const [statusFilter, setStatusFilter]   = useState('all');
  const [searchTerm, setSearchTerm]       = useState('');
  const [page, setPage]       = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showQuickRuns, setShowQuickRuns] = useState(false);

  const activeItem = SECTIONS.find(s => s.key === activeSection)!;

  const loadTestResults = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/test-results/results');
      if (res.data.success) {
        setTestResults(res.data.data.testResults || []);
        setTestSuites(res.data.data.testSuites  || []);
      } else {
        setToast({ type: 'error', message: res.data.error || 'Failed to load test results' });
      }
    } catch { setToast({ type: 'error', message: 'Failed to load test results' }); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { loadTestResults(); }, [loadTestResults]);
  useEffect(() => { setPage(1); }, [activeSection, searchTerm, statusFilter, pageSize]);

  const runTests = async (path: string) => {
    setRunning(true);
    try {
      const res = await apiClient.post('/api/test-results/run', { test_path: path });
      if (res.data.success) {
        const d = res.data.data;
        if (d.tests) setTestResults(d.tests);
        if (d.summary) {
          const s = d.summary;
          const suite: TestSuite = {
            name: 'All Tests', totalTests: s.total, passedTests: s.passed,
            failedTests: s.failed, skippedTests: s.skipped,
            duration: s.duration, status: s.status, timestamp: new Date().toISOString(),
          };
          setTestSuites(prev => [suite, ...prev.slice(0, 4)]);
        }
      } else {
        setToast({ type: 'error', message: res.data.error || `Failed to run: ${path}` });
      }
    } catch { setToast({ type: 'error', message: `Failed to run: ${path}` }); }
    finally  { setRunning(false); }
  };

  const filteredResults = testResults.filter(r => {
    const matchesSection = activeSection === 'ALL' || r.category === activeSection.toLowerCase();
    const matchesStatus  = statusFilter === 'all'  || r.status.toLowerCase() === statusFilter;
    const matchesSearch  = !searchTerm || r.testName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSection && matchesStatus && matchesSearch;
  });

  const totalRecords  = filteredResults.length;
  const paginatedRows = filteredResults.slice((page - 1) * pageSize, page * pageSize);


  return (
    <>
      <div className="netbody-card flex flex-col h-full">

        {/* Toolbar — Show LEFT · Quick Runs · KPI stats · Category + Status + Search + Run RIGHT */}
        <div className="netbody-header">
          {/* Quick Runs toggle */}
          <div className="relative">
            <button
              onClick={() => setShowQuickRuns(v => !v)}
              title="Quick run filters"
              data-testid="BTN_test_results_quick_runs_toggle"
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-xs font-medium transition-colors ${
                showQuickRuns
                  ? 'bg-purple-600 border-purple-600 text-white'
                  : 'bg-white border-gray-300 text-gray-600 hover:border-purple-400 hover:text-purple-600'
              }`}
            >
              <SlidersHorizontal size={13} />
            </button>

            {showQuickRuns && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowQuickRuns(false)} />
                <div
                  className="absolute left-0 top-full mt-2 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-3 flex flex-col gap-2 min-w-[160px]"
                  style={{ animation: 'fadeSlideDown 0.18s ease' }}
                >
                  {QUICK_RUNS.map(([label, path]) => (
                    <button
                      key={label}
                      disabled={running}
                      onClick={() => { runTests(path); setShowQuickRuns(false); }}
                      data-testid={`BTN_test_results_quick_run_${label.toLowerCase().replace(/\s+/g, '_')}`}
                      className="flex items-center gap-2 px-3 py-2 rounded text-xs font-medium text-gray-700 bg-gray-50 hover:bg-purple-50 hover:text-purple-700 border border-gray-200 hover:border-purple-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    >
                      <Play size={11} />
                      {label}
                    </button>
                  ))}
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <button
                      disabled={loading}
                      onClick={() => { loadTestResults(); setShowQuickRuns(false); }}
                      data-testid="BTN_test_results_refresh"
                      className="flex items-center gap-2 px-3 py-2 rounded text-xs font-medium text-gray-700 bg-gray-50 hover:bg-blue-50 hover:text-blue-700 border border-gray-200 hover:border-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full text-left"
                    >
                      <RefreshCw size={11} />
                      Refresh
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {/* Category dropdown — replaces the left nav */}
            <FormControl.SearchSelect
              value={activeSection}
              onChange={v => setActiveSection((v ?? 'ALL') as SectionKey)}
              className="filter-status"
              data-testid="SEL_test_results_category"
              noSearchIcon
              options={SECTIONS.map(s => ({ value: s.key, label: s.label }))}
            />

            <FormControl.SearchSelect
              value={statusFilter}
              onChange={v => setStatusFilter(String(v ?? 'all'))}
              className="filter-status"
              data-testid="SEL_test_results_status"
              noSearchIcon
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'passed', label: 'Passed' },
                { value: 'failed', label: 'Failed' },
                { value: 'running', label: 'Running' },
                { value: 'skipped', label: 'Skipped' },
              ]}
            />

            <FormControl.Input
              type="text"
              placeholder={`Search ${activeItem.label.toLowerCase()}…`}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="filter-search"
              data-testid="INP_test_results_search"
            />

            <Button variant="fill" icon={Play} onClick={() => runTests('tests/')} disabled={running} data-testid="BTN_test_results_run_all">
              {running ? 'Running…' : 'Run All Tests'}
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="netbody-content">

          {/* Test Suites Summary */}
          {testSuites.length > 0 && (
            <div className="mx-6 mb-4">
              <h4 className="text-[13px] font-semibold text-gray-700 mb-2">Test Suites Summary</h4>
              <div className="scroll-container" style={{ height: 'auto', maxHeight: '180px' }}>
                <div className="table-list">
                  <div className="table-list-scroll">
                    <table className="table-list-table">
                      <thead>
                        <tr>
                          <th className="table-list-th">Suite</th>
                          <th className="table-list-th">Total</th>
                          <th className="table-list-th">Passed</th>
                          <th className="table-list-th">Failed</th>
                          <th className="table-list-th">Skipped</th>
                          <th className="table-list-th">Duration</th>
                          <th className="table-list-th table-list-cell--status">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testSuites.map((suite, i) => (
                          <tr key={i} className="table-list-row">
                            <td className="table-list-cell"><span className="table-list-cell-title">{suite.name}</span></td>
                            <td className="table-list-cell">{suite.totalTests}</td>
                            <td className="table-list-cell text-green-600 font-medium">{suite.passedTests}</td>
                            <td className="table-list-cell text-red-600 font-medium">{suite.failedTests}</td>
                            <td className="table-list-cell text-yellow-600">{suite.skippedTests}</td>
                            <td className="table-list-cell">{suite.duration}s</td>
                            <td className="table-list-cell">
                              <span className={statusBadgeCss(suite.status)}>{suite.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Individual Test Results */}
          <div className="scroll-container flex-1 overflow-hidden">
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Test Name</th>
                      <th className="table-list-th">Category</th>
                      <th className="table-list-th">Status</th>
                      <th className="table-list-th">Duration</th>
                      <th className="table-list-th">File</th>
                      <th className="table-list-th">Message</th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loading} aria-live="polite">
                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'pill', 'pill', '2xs', 'sm', 'md']} />}
                    {!loading && filteredResults.length === 0 && (
                      <tr>
                        <td colSpan={7} className="table-list-empty-cell">
                          No {activeItem.label.toLowerCase()} found. Click "Run All Tests" to run the test suite.
                        </td>
                      </tr>
                    )}
                    {!loading && paginatedRows.map((r, idx) => (
                      <tr
                        key={r.id}
                        className="table-list-row"
                        onClick={() => runTests(r.testFile)}
                        title="Click to re-run this test"
                        data-testid={`BTN_test_results_row_${r.id}_run`}
                      >
                        <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title">{r.testName}</span>
                        </td>
                        <td className="table-list-cell">
                          <span className="status-badge xs info">
                            {r.category}
                          </span>
                        </td>
                        <td className="table-list-cell">
<span className={statusBadgeCss(r.status)}>{r.status}</span>
                        </td>
                        <td className="table-list-cell">{r.duration}s</td>
                        <td className="table-list-cell table-list-cell--truncate" title={r.testFile}>
                          {r.testFile.replace(/^.*AnyGRC\//, '')}
                        </td>
                        <td className="table-list-cell table-list-cell--truncate" title={r.message}>
                          {r.message ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                pageSize={pageSize}
                totalRecords={totalRecords}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                recordLabel={activeItem.label.toLowerCase()}
              />
            </div>
          </div>

        </div>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default TestResultsTab;
